import React, { useState, useEffect, useRef } from "react";

const API = process.env.REACT_APP_API_URL;

// ─── Keyframes injectées une seule fois ───────────────────────────────────────
const KEYFRAMES = `
  @keyframes obj-scan { 0%{transform:translateY(-100%)} 100%{transform:translateY(3000%)} }
  @keyframes obj-blink { 0%,100%{opacity:1} 49%{opacity:1} 50%{opacity:0} 99%{opacity:0} }
  @keyframes obj-ticker { 0%{transform:translateX(100%)} 100%{transform:translateX(-100%)} }
  @keyframes obj-recv { 0%{opacity:0;letter-spacing:.35em} 100%{opacity:1;letter-spacing:.01em} }
  @keyframes obj-pulse-ring {
    0%{box-shadow:0 0 0 0 rgba(251,191,36,.45)}
    70%{box-shadow:0 0 0 5px rgba(251,191,36,0)}
    100%{box-shadow:0 0 0 0 rgba(251,191,36,0)}
  }
`;

// ─── Objectif actif ───────────────────────────────────────────────────────────
function ActiveObjective({ step, isNew }) {
  const [open, setOpen] = useState(true);

  // Rouvrir automatiquement quand un nouvel objectif arrive
  useEffect(() => { if (isNew) setOpen(true); }, [isNew]);

  if (!step) return null;

  const hasTodo = step.todo && step.todo.length > 0;

  // ── Replié ──
  if (!open) {
    return (
      <div
        onClick={() => setOpen(true)}
        className="relative overflow-hidden rounded-[7px] cursor-pointer select-none"
        style={{
          border: isNew ? "1px solid rgba(251,191,36,.7)" : "1px solid rgba(251,191,36,.3)",
          background: "rgba(10,8,0,.9)",
          animation: isNew ? "obj-pulse-ring 1.5s ease-in-out infinite" : "none",
        }}
      >
        {/* barre gauche */}
        <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-[7px]"
          style={{ background: isNew ? "rgba(251,191,36,1)" : "rgba(251,191,36,.7)" }} />

        <div className="flex items-center h-7">
          {/* label fixe */}
          <div className="flex items-center gap-1.5 px-2 shrink-0 border-r"
            style={{ borderColor: isNew ? "rgba(251,191,36,.3)" : "rgba(251,191,36,.2)" }}>
            <span className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{
                background: isNew ? "rgba(251,191,36,1)" : "rgba(251,191,36,.85)",
                animation: isNew ? "obj-blink .6s step-end infinite" : "obj-blink 1s step-end infinite",
              }} />
            <span className="text-[8px] font-bold uppercase tracking-[.2em] whitespace-nowrap"
              style={{ color: isNew ? "rgba(251,191,36,.9)" : "rgba(251,191,36,.7)" }}>
              MISSION
            </span>
          </div>

          {/* ticker */}
          <div className="flex-1 overflow-hidden relative h-full flex items-center">
            <span className="whitespace-nowrap inline-block text-[10px] font-semibold pl-3"
              style={{
                animation: "obj-ticker 14s linear infinite",
                color: isNew ? "rgba(251,191,36,.95)" : "rgba(255,255,255,.8)",
                letterSpacing: ".02em",
              }}>
              {isNew ? "◈ Nouvel objectif — " : ""}{step.text}
            </span>
          </div>

          {/* droite */}
          <div className="flex items-center gap-1.5 px-2 shrink-0">
            {isNew && (
              <span className="text-[7px] font-bold uppercase tracking-[.1em] px-1.5 py-0.5 rounded"
                style={{ background: "rgba(251,191,36,.2)", border: "1px solid rgba(251,191,36,.5)", color: "rgba(251,191,36,.9)" }}>
                NEW
              </span>
            )}
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 3.5l3 3 3-3" stroke="rgba(251,191,36,.5)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
    );
  }

  // ── Déplié ──
  return (
    <div className="relative overflow-hidden rounded-lg"
      style={{ border: "1px solid rgba(251,191,36,.35)", background: "rgba(5,4,0,.95)" }}>
      {/* scanline */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-lg">
        <div className="absolute left-0 right-0 h-px"
          style={{ background: "linear-gradient(90deg,transparent,rgba(251,191,36,.1),transparent)", animation: "obj-scan 4s linear infinite" }} />
      </div>
      {/* barre gauche */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 rounded-l-lg"
        style={{ background: "rgba(251,191,36,.7)" }} />

      {/* Header cliquable */}
      <button type="button" onClick={() => setOpen(false)}
        className="w-full flex items-center justify-between px-3 py-1.5 border-b"
        style={{ borderColor: "rgba(251,191,36,.15)", background: "rgba(251,191,36,.06)" }}>
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full shrink-0"
            style={{ background: "rgba(251,191,36,.8)", animation: "obj-blink 1s step-end infinite" }} />
          <span className="text-[8px] font-bold uppercase tracking-[.22em]"
            style={{ color: "rgba(251,191,36,.75)" }}>
            Objectif actif
          </span>
          {isNew && (
            <span className="text-[7px] font-bold uppercase tracking-[.1em] px-1.5 py-0.5 rounded"
              style={{ background: "rgba(251,191,36,.2)", border: "1px solid rgba(251,191,36,.5)", color: "rgba(251,191,36,.9)" }}>
              NEW
            </span>
          )}
        </div>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ transform: "rotate(180deg)" }}>
          <path d="M2 3.5l3 3 3-3" stroke="rgba(251,191,36,.45)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Corps */}
      <div className="px-3 py-2.5">
        <p key={step.key} className="text-[12px] font-bold leading-snug"
          style={{ color: "rgba(255,255,255,.95)", letterSpacing: ".01em", animation: "obj-recv .5s ease-out" }}>
          {step.text}
        </p>

        {hasTodo && (
          <>
            <div className="my-2 h-px"
              style={{ background: "linear-gradient(90deg,rgba(251,191,36,.2),transparent)" }} />
            <div className="flex flex-col gap-1">
              {step.todo.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="shrink-0 text-[10px] mt-0.5" style={{ color: "rgba(251,191,36,.45)" }}>›</span>
                  <span
                    className="text-[10px] leading-snug font-mono"
                    style={{ color: "rgba(255,255,255,.6)" }}
                    dangerouslySetInnerHTML={{
                      __html: (item?.label ?? "").replace(
                        /`([^`]+)`/g,
                        '<span style="background:rgba(251,191,36,.12);padding:1px 5px;border-radius:3px;color:rgba(251,191,36,.9)">$1</span>'
                      ),
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ScenarioTracker principal ────────────────────────────────────────────────
export default function ScenarioTracker({ sessionCode }) {
  const [steps, setSteps] = useState([]);
  const [unlockedKeys, setUnlockedKeys] = useState([]);
  const [newlyUnlocked, setNewlyUnlocked] = useState(new Set());
  const [collapsed, setCollapsed] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);

  const everSeenKeysRef = useRef(new Set());
  const scrollContainerRef = useRef(null);
  const stepRefs = useRef({});

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await fetch(`${API}/api/sessions/${sessionCode}/scenario-tracker`);
        const data = await res.json();
        setSteps(data.steps || []);
        const nextUnlocked = data.unlockedKeys || [];
        const justUnlocked = new Set();
        for (const key of nextUnlocked) {
          if (!everSeenKeysRef.current.has(key)) {
            justUnlocked.add(key);
            everSeenKeysRef.current.add(key);
          }
        }
        setUnlockedKeys(nextUnlocked);
        if (justUnlocked.size > 0) {
          setNewlyUnlocked(justUnlocked);
          setUnseenCount(prev => prev + justUnlocked.size);
          setTimeout(() => setNewlyUnlocked(new Set()), 12000);
        }
      } catch (err) { console.error("Scenario tracker fetch error:", err); }
    };
    fetchProgress();
    const itv = setInterval(fetchProgress, 2000);
    return () => clearInterval(itv);
  }, [sessionCode]);

  useEffect(() => {
    if (newlyUnlocked.size === 0) return;
    const firstNewKey = [...newlyUnlocked][0];
    const el = stepRefs.current[firstNewKey];
    if (el && scrollContainerRef.current) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [newlyUnlocked]);

  const handleToggle = () => {
    setCollapsed(prev => { if (prev) setUnseenCount(0); return !prev; });
  };

  const unlockedSet = new Set(unlockedKeys);
  const totalSteps = steps.length;
  const unlockedCount = unlockedKeys.length;
  const progressPct = totalSteps > 0 ? Math.round((unlockedCount / totalSteps) * 100) : 0;

  // Dernier step débloqué non-intro = objectif actif
  const unlockedSteps = steps.filter(s => s.unlocked || unlockedSet.has(s.key));
  const activeStep = unlockedSteps.length > 0 ? unlockedSteps[unlockedSteps.length - 1] : null;
  const isActiveNew = activeStep ? newlyUnlocked.has(activeStep.key) : false;
  // const isIntroOnly = activeStep?.key?.startsWith("intro.");
  const isIntroOnly = activeStep?.key?.startsWith("intro.") && !activeStep?.todo?.length;

  return (
    <div className="relative flex flex-col gap-1.5">
      <style>{KEYFRAMES}</style>

      {/* ── Objectif actif ── */}
      {activeStep && !isIntroOnly && (
        <ActiveObjective step={activeStep} isNew={isActiveNew} />
      )}

      {/* ── Journal collapsible ── */}
      <div className="relative">
        <button type="button" onClick={handleToggle} className="w-full">
          <div className={[
            "relative flex items-center justify-between gap-2 px-3 py-2 rounded-xl border transition-all duration-200",
            collapsed
              ? "border-cyan-500/30 bg-slate-950/60 hover:border-cyan-400/50 hover:bg-slate-900/60"
              : "border-cyan-500/30 bg-gradient-to-r from-slate-950/90 to-slate-900/80 rounded-b-none border-b-0",
          ].join(" ")}>
            <div className="flex items-center gap-2 min-w-0">
              <div className="relative shrink-0">
                <div className="absolute inset-0 blur-sm bg-cyan-400/30 rounded-full" />
                <span className="relative text-sm">📖</span>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[.22em] text-cyan-400/80 font-semibold leading-none">
                  Journal
                </p>
                <div className="mt-1 w-16 h-1 rounded-full bg-slate-700/60 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progressPct}%`,
                      background: "linear-gradient(90deg, rgba(6,182,212,0.8), rgba(52,211,153,0.7))",
                      boxShadow: progressPct > 0 ? "0 0 4px rgba(6,182,212,0.5)" : "none",
                    }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {collapsed && unseenCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-cyan-500/25 border border-cyan-400/60 text-cyan-300 animate-pulse uppercase tracking-wider">
                  +{unseenCount}
                </span>
              )}
              <span className="text-[10px] font-mono text-slate-400 tabular-nums">
                {unlockedCount}<span className="text-slate-600">/{totalSteps}</span>
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"
                className={["text-cyan-400/60 transition-transform duration-200", collapsed ? "rotate-0" : "rotate-180"].join(" ")}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </button>

        {!collapsed && (
          <div ref={scrollContainerRef}
            className="relative max-h-44 overflow-y-auto rounded-b-xl rounded-t-none border border-t-0 border-cyan-500/20 bg-gradient-to-b from-slate-950/90 to-slate-900/90 backdrop-blur-sm px-3 pt-2 pb-3 shadow-[inset_0_1px_4px_rgba(0,0,0,0.3)] scenario-scrollbar">
            <div className="absolute inset-0 pointer-events-none opacity-5"
              style={{ backgroundImage: "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(6,182,212,0.1) 2px,rgba(6,182,212,0.1) 4px)" }} />
            <div className="relative space-y-2">
              {steps.map(step => {
                const isUnlocked = step.unlocked || unlockedSet.has(step.key);
                const isNew = newlyUnlocked.has(step.key);
                const isActive = activeStep?.key === step.key && !isIntroOnly;
                return (
                  <div key={step.key} ref={el => { stepRefs.current[step.key] = el; }}
                    className={["relative text-xs leading-relaxed transition-all duration-500 font-mono", isUnlocked ? "opacity-100" : "opacity-40"].join(" ")}>
                    {isUnlocked ? (
                      <div className="flex items-start gap-2">
                        <div className={[
                          "shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-all duration-300 mt-0.5",
                          isActive
                            ? "border-amber-400/70 bg-amber-500/20"
                            : isNew
                            ? "border-cyan-400 bg-cyan-500/20 shadow-[0_0_8px_rgba(6,182,212,0.6)]"
                            : "border-emerald-400/60 bg-emerald-500/10",
                        ].join(" ")}>
                          <span className={[
                            "text-[10px] transition-all",
                            isActive ? "text-amber-400" : isNew ? "text-cyan-300" : "text-emerald-400",
                          ].join(" ")}>
                            {isActive ? "▶" : "✓"}
                          </span>
                        </div>
                        <span className={[
                          "flex-1 transition-all duration-300",
                          isActive ? "text-amber-200 font-semibold" : isNew ? "text-cyan-200 font-semibold" : "text-slate-300",
                        ].join(" ")}>
                          {step.text}
                        </span>
                        {isNew && !isActive && (
                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[8px] font-bold bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 animate-pulse uppercase tracking-wider">
                            New
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <div className="shrink-0 w-4 h-4 rounded border border-slate-700/50 bg-slate-800/30 mt-0.5" />
                        <div className="flex-1 h-4 mt-0.5 rounded bg-slate-800/50 relative overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-700/30 to-transparent animate-pulse" style={{ animationDuration: "2s" }} />
                          <div className="absolute inset-0 flex items-center px-2">
                            <span className="text-slate-700 text-[10px]">█████████</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {steps.length === 0 && (
                <div className="text-center py-6">
                  <div className="text-slate-600 text-xs font-mono">[ Aucune donnée ]</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .scenario-scrollbar::-webkit-scrollbar { width: 5px; }
        .scenario-scrollbar::-webkit-scrollbar-track { background: rgba(15,23,42,0.5); border-radius: 3px; }
        .scenario-scrollbar::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.3); border-radius: 3px; border: 1px solid rgba(6,182,212,0.2); }
        .scenario-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(6,182,212,0.5); }
      `}</style>
    </div>
  );
}